import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import Modal from 'react-modal';
import { supabase } from '../utils/supabaseClient';
import VerticalGauge from './VerticalGauge';
import { fetchQuotesServer } from '../utils/fetchers';
import QuickLookModal from "./QuickLookModal";
import QuickLookChart from "./QuickLookChart";

Modal.setAppElement('#root');

const FREE_CAP = 8;
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;

// --- symbol helpers ---
const FIAT = new Set([
  'USD','EUR','GBP','JPY','AUD','CAD','CHF','NZD','CNY','SEK','NOK','DKK','ZAR','MXN','PLN','HUF','CZK','TRY','BRL','INR','SGD','HKD','ILS','RUB','KRW','TWD','THB','MYR','IDR','PHP','AED','SAR','CLP','COP','ARS','PEN','XAU','XAG'
]);

function classifySlashSymbol(symRaw) {
  const [base, quote] = String(symRaw).toUpperCase().split('/');
  if (!base || !quote) return null;
  const isFx = FIAT.has(base) && FIAT.has(quote);
  return {
    type: isFx ? 'fx' : 'crypto',
    symbol: `${base}${quote}`,
    base, quote,
    currency: quote
  };
}

function parseCryptoPair(pair = '') {
  const m = String(pair).trim().toUpperCase().match(/^([A-Z0-9]{2,10})(USD|USDT|EUR|GBP|JPY|AUD|CAD|CHF)$/);
  return m ? { base: m[1], quote: m[2] } : null;
}
function parseFxPair(pair = '') {
  const CUR = '(USD|EUR|GBP|JPY|AUD|CAD|CHF|NZD|CNY|SEK|NOK|DKK|ZAR|MXN|PLN|HUF|CZK|TRY|BRL|INR|SGD|HKD|ILS|RUB|KRW|TWD|THB|MYR|IDR|PHP|AED|SAR|CLP|COP|ARS|PEN|XAU|XAG)';
  const m = String(pair).trim().toUpperCase().match(new RegExp(`^${CUR}${CUR}$`));
  return m ? { base: m[1], quote: m[2] } : null;
}

// --- tiny hook for media queries ---
function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

const Dashboard = ({ session }) => {
  const [tickers, setTickers] = useState([]);
  const [tickerData, setTickerData] = useState({});
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [sortBy, setSortBy] = useState('drawdown');
  const [refreshing, setRefreshing] = useState({});
  const [previewSymbol, setPreviewSymbol] = useState(null);

  // NEW: which card’s warning popup is open
  const [openWarnFor, setOpenWarnFor] = useState(null);

  // responsive flags
  const isPhone = useMediaQuery('(max-width: 640px)');
  const isTablet = useMediaQuery('(max-width: 900px)');

  // fetch or create the user's row; merge duplicates if any
  const ensureRow = useCallback(async () => {
    const { data: rows, error } = await supabase
      .from('user_tickers')
      .select('id, created_at, tickers')
      .eq('user_id', session.user.id);

    if (error) throw error;

    if (!rows || rows.length === 0) {
      const { error: insErr } = await supabase
        .from('user_tickers')
        .insert({ user_id: session.user.id, tickers: [] });
      if (insErr) throw insErr;
      return [];
    }

    if (rows.length === 1) {
      const current = rows[0].tickers || [];
      let changed = false;
      const fixed = current.map(t => {
        if (t?.symbol && t.symbol.includes('/')) {
          const raw = String(t.symbol).toUpperCase();
          const noslash = raw.replace('/', '');
          changed = true;
          const [base, quote] = raw.split('/');
          const assetType = (base && base.length !== 3) ? 'crypto' : 'fx';
          return { ...t, symbol: noslash, base, quote, assetType };
        }
        return t;
      });
      if (changed) {
        await supabase.from('user_tickers')
          .update({ tickers: fixed })
          .eq('id', rows[0].id);
      }
      return changed ? fixed : current;
    }

    const all = [];
    for (const r of rows) {
      const arr = Array.isArray(r.tickers) ? r.tickers : [];
      for (const t of arr) if (t?.symbol) all.push(t);
    }
    const bySymbol = new Map();
    for (const t of all) {
      const key = String(t.symbol).toUpperCase();
      if (!bySymbol.has(key)) bySymbol.set(key, { ...t, symbol: key });
    }
    const mergedTickers = Array.from(bySymbol.values());

    const keeper = rows.slice().sort((a, b) => {
      const da = new Date(a.created_at || 0).getTime();
      const db = new Date(b.created_at || 0).getTime();
      if (isFinite(da) && isFinite(db) && da !== db) return da - db;
      return String(a.id).localeCompare(String(b.id));
    })[0];

    const { error: updErr } = await supabase
      .from('user_tickers')
      .update({ tickers: mergedTickers })
      .eq('id', keeper.id);
    if (updErr) console.warn('Could not update keeper row:', updErr?.message);

    return mergedTickers;
  }, [session.user.id]);

  // Load tickers on mount/login
  useEffect(() => {
    (async () => {
      try {
        const t = await ensureRow();
        setTickers(t);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [ensureRow]);

  // Fetch data for current tickers from server-side cache/queue
  useEffect(() => {
    if (!tickers.length) return;

    const load = async () => {
      try {
        const { data: { session: fresh } } = await supabase.auth.getSession();
        const token = fresh?.access_token;
        const syms = tickers.map(t => t.symbol.toUpperCase());
        const data = await fetchQuotesServer(syms, session.user.id, token, false);
        setTickerData(data);
      } catch (e) {
        console.error('Server quote fetch error', e);
      }
    };

    load();
  }, [tickers, session.user.id]);

  // Save tickers
  const saveTickers = async (newTickers) => {
    const { error } = await supabase
      .from('user_tickers')
      .upsert(
        { user_id: session.user.id, tickers: newTickers },
        { onConflict: 'user_id' }
      );
    if (error) console.error(error);
  };

  // Adders
  const addStockTicker = (symbol) => {
    const raw = symbol.toUpperCase().trim();
    if (raw.includes('/')) {
      const info = classifySlashSymbol(raw);
      if (!info) return;
      if (info.type === 'crypto') return addCryptoPair(info.symbol);
      return addFxPair(info.symbol);
    }
    if (tickers.find((t) => t.symbol === raw)) return;
    if (tickers.length >= FREE_CAP) {
      alert(`Free plan allows up to ${FREE_CAP} tickers. Remove one or upgrade to add more.`);
      return;
    }
    const newTickers = [...tickers, { symbol: raw, assetType: 'stock', timePeriod: '1y' }];
    setTickers(newTickers);
    saveTickers(newTickers);
    setSearchModalOpen(false);
  };

  const addCryptoPair = (pair) => {
    const norm = pair.toUpperCase().trim();
    if (tickers.find((t) => t.symbol === norm)) return;
    if (tickers.length >= FREE_CAP) {
      alert(`Free plan allows up to ${FREE_CAP} tickers. Remove one or upgrade to add more.`);
      return;
    }
    const p = parseCryptoPair(norm);
    if (!p) return;
    const newTickers = [
      ...tickers,
      { symbol: `${p.base}${p.quote}`, assetType: 'crypto', base: p.base, quote: p.quote, timePeriod: '1y' },
    ];
    setTickers(newTickers);
    saveTickers(newTickers);
    setSearchModalOpen(false);
  };

  const addFxPair = (pair) => {
    const norm = pair.toUpperCase().trim();
    if (tickers.find((t) => t.symbol === norm)) return;
    if (tickers.length >= FREE_CAP) {
      alert(`Free plan allows up to ${FREE_CAP} tickers. Remove one or upgrade to add more.`);
      return;
    }
    const p = parseFxPair(norm);
    if (!p) return;
    const newTickers = [
      ...tickers,
      { symbol: `${p.base}${p.quote}`, assetType: 'fx', base: p.base, quote: p.quote, timePeriod: '1y' },
    ];
    setTickers(newTickers);
    saveTickers(newTickers);
    setSearchModalOpen(false);
  };

  // Remove
  const removeTicker = (symbol) => {
    const newTickers = tickers.filter((t) => t.symbol !== symbol);
    setTickers(newTickers);
    saveTickers(newTickers);
  };

  // Search
  const handleSearch = async () => {
    setSearchResults([]);
    const q = searchQuery.trim();
    if (!q) return;

    try {
      // get a fresh access token for the call
      let token = session?.access_token;
      if (!token) {
        const { data: { session: s } } = await supabase.auth.getSession();
        token = s?.access_token;
      }

      const url = `${SUPABASE_URL}/functions/v1/yahoo-search?q=${encodeURIComponent(q)}`;
      const { data } = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // normalize & de-dupe (the function already does most of it)
      const seen = new Set();
      const uniq = [];
      for (const r of data?.results || []) {
        if (!seen.has(r.symbol)) {
          seen.add(r.symbol);
          uniq.push({ ...r, source: 'Yahoo Finance' });
        }
      }

      // Optional availability/validation via your existing server quotes
      try {
        const syms = uniq.map(r => r.symbol);
        const validation = await fetchQuotesServer(syms, null, token, { force: true, validate: true });
        for (const row of uniq) row._unsupported = !!validation[row.symbol]?.unsupported_free;
      } catch (e) {
        console.warn("availability check failed", e);
      }

      setSearchResults(uniq);
    } catch (e) {
      console.warn('Yahoo search failed', e);
    }
  };

  // helper: compute drawdown
  const computeDrawdownPct = (d) => {
    const price = d?.close != null ? Number(d.close) : null;
    const high52 = d?.fifty_two_week_high != null ? Number(d.fifty_two_week_high) : null;
    const hasDrawdown = Number.isFinite(price) && Number.isFinite(high52) && high52 > 0;
    if (!hasDrawdown) return null;
    return Math.max(0, Math.min(100, ((high52 - price) / high52) * 100));
  };

  const sorted = useMemo(() => {
    const copy = [...tickers];
    if (sortBy === 'drawdown') {
      copy.sort((a, b) => {
        const da = computeDrawdownPct(tickerData[a.symbol]) ?? 0;
        const db = computeDrawdownPct(tickerData[b.symbol]) ?? 0;
        return da - db;
      });
    }
    return copy;
  }, [tickers, tickerData, sortBy]);

  // refresh
  const refreshTicker = async (symbol) => {
    try {
      setRefreshing((m) => ({ ...m, [symbol]: true }));
      const { data: { session: fresh } } = await supabase.auth.getSession();
      const token = fresh?.access_token;
      const partial = await fetchQuotesServer([symbol], session.user.id, token, true);
      setTickerData((prev) => ({ ...prev, ...partial }));
    } catch (e) {
      console.error('refresh error', e);
    } finally {
      setRefreshing((m) => ({ ...m, [symbol]: false }));
    }
  };

  const atCap = tickers.length >= FREE_CAP;

  // ---- responsive sizes ----
  const gaugeSize = isPhone
    ? { width: 56, height: 160 }
    : isTablet
      ? { width: 72, height: 200 }
      : { width: 80, height: 240 };
  const containerMax = isPhone ? '100%' : 1200;

  // Force two columns on phones so we always see two gauges side by side.
  const phoneColumns = 'repeat(2, minmax(0, 1fr))';

  return (
    <div style={{ padding: isPhone ? 12 : 20, background: '#121212', minHeight: '100vh', color: '#fff' }}>
      {/* centered container with responsive side padding */}
      <div style={{ maxWidth: containerMax, margin: "0 auto", padding: isPhone ? "8px 8px 24px" : "20px 24px" }}>

        {/* Controls */}
        <div
          style={{
            margin: isPhone ? "8px 0 12px" : "12px 0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          {/* LEFT: sort */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label style={{ fontSize: 14, color: "#ddd" }} htmlFor="sortSel">Sort:</label>
            <select
              id="sortSel"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                background: "#1b1b1b",
                color: "#fff",
                border: "1px solid #3a3a3a",
                borderRadius: 8,
                padding: "8px 10px",
              }}
            >
              <option value="drawdown">By Drawdown</option>
            </select>
          </div>

          {/* RIGHT: x/max label + +Add button */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 12, color: "#bbb" }}>
              {tickers.length}/{FREE_CAP}
            </span>
            <button
              onClick={() => !atCap && setSearchModalOpen(true)}
              disabled={atCap}
              title={atCap ? `Max ${FREE_CAP} tickers reached` : "Add a symbol"}
              style={{
                background: "#2a2a2a",
                color: "#fff",
                borderRadius: 10,
                padding: "10px 12px",
                border: "1px solid #3a3a3a",
                cursor: atCap ? "not-allowed" : "pointer",
                opacity: atCap ? 0.5 : 1,
              }}
            >
              + Add
            </button>
          </div>
        </div>

        {/* Cards grid (clicking anywhere closes warning popups) */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isPhone ? phoneColumns : 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: isPhone ? 10 : 16
          }}
          onClick={() => setOpenWarnFor(null)}
        >
          {sorted.map((t) => {
            const d = tickerData[t.symbol] || {};
            const price = d?.close != null ? Number(d.close) : null;
            const high52 = d?.fifty_two_week_high != null ? Number(d.fifty_two_week_high) : null;
            const hasDrawdown = Number.isFinite(price) && Number.isFinite(high52) && high52 > 0;
            const ddPct = hasDrawdown ? ((high52 - price) / high52) * 100 : null;
            const drawdownPct = hasDrawdown ? Math.max(0, Math.min(100, ddPct)) : 0;
            const tooltip = hasDrawdown
              ? `${ddPct.toFixed(2)}% from 52w high`
              : (d?.error || 'No 52w data');

            const updatedAt = d?.updated_at ? new Date(d.updated_at) : null;
            const updatedAtStr = updatedAt ? updatedAt.toLocaleString() : null;
            const stale5m = !!d?.stale_5m;

            return (
              <div
                key={t.symbol}
                style={{
                  background: '#1b1b1b',
                  padding: isPhone ? 10 : 12,
                  borderRadius: 12,
                  minWidth: 0,
                  textAlign: 'center',
                  boxShadow: '0 1px 0 rgba(255,255,255,0.04), inset 0 0 0 1px rgba(255,255,255,0.04)',
                  position: 'relative', // anchor for popup
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header row: ticker • price  +  (optional warning)  +  chart icon */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    marginBottom: 6,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: "1 1 auto" }}>
                    <div
                      style={{
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontSize: isPhone ? 14 : 16,
                        minWidth: 0,
                        textAlign: "left",
                        paddingLeft: isPhone ? 6 : 8,
                      }}
                      title={t.symbol}
                    >
                      {t.symbol} {price != null ? `• $${price.toFixed(2)}` : "• …"}
                    </div>

                    {/* Warning icon (only when stale) */}
                    {stale5m && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setOpenWarnFor(openWarnFor === t.symbol ? null : t.symbol); }}
                        title="Price may be delayed. Click for details."
                        aria-label={`Price not current for ${t.symbol}`}
                        style={{
                          width: isPhone ? 22 : 24,
                          height: isPhone ? 22 : 24,
                          minWidth: isPhone ? 22 : 24,
                          display: "grid",
                          placeItems: "center",
                          background: "transparent",
                          border: "1px solid rgba(255,183,77,0.35)",
                          borderRadius: 6,
                          cursor: "pointer",
                          lineHeight: 0,
                          padding: 0,
                          color: "#ffb74d",
                        }}
                      >
                        <svg
                          width={isPhone ? 14 : 16}
                          height={isPhone ? 14 : 16}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                          focusable="false"
                          style={{ display: "block" }}
                        >
                          <path d="M12 3L2 20h20L12 3z" />
                          <path d="M12 9v5" />
                          <circle cx="12" cy="17" r="1" fill="currentColor" stroke="none" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* Chart icon button */}
                  <button
                    onClick={() => setPreviewSymbol(t.symbol)}
                    title="Preview chart"
                    aria-label={`Preview chart for ${t.symbol}`}
                    style={{
                      width: isPhone ? 28 : 32,
                      height: isPhone ? 28 : 32,
                      minWidth: isPhone ? 28 : 32,
                      display: "grid",
                      placeItems: "center",
                      background: "#1f1f1f",
                      color: "#ddd",
                      border: "1px solid #3a3a3a",
                      borderRadius: 8,
                      cursor: "pointer",
                      flex: "0 0 auto",
                      padding: 0,
                      lineHeight: 0,
                    }}
                  >
                    <svg
                      width={isPhone ? 16 : 18}
                      height={isPhone ? 16 : 18}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                      focusable="false"
                      style={{ display: "block" }}
                    >
                      <path d="M4 20V5" />
                      <path d="M4 20H20" />
                      <path d="M6 16l4-4 3 2 5-6" />
                      <circle cx="18" cy="8" r="0.9" fill="currentColor" stroke="none" />
                    </svg>
                  </button>
                </div>

                {/* Warning popup (replaces old inline banner) */}
                {openWarnFor === t.symbol && (
                  <div
                    role="dialog"
                    aria-label="Price staleness info"
                    style={{
                      position: "absolute",
                      top: isPhone ? 40 : 44,
                      left: 10,
                      right: 10,
                      background: "#1f1f1f",
                      border: "1px solid #555",
                      borderRadius: 10,
                      padding: 12,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                      zIndex: 2,
                      boxShadow: "0 10px 24px rgba(0,0,0,0.35)",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div style={{ fontSize: 12, color: "#ffb74d" }}>
                      Not current{updatedAtStr ? ` • ${updatedAtStr}` : ""}
                    </div>
                    
                    <button
                      onClick={() => refreshTicker(t.symbol)}
                      disabled={!!refreshing[t.symbol]}
                      title="Force refresh now"
                      aria-label="Refresh"
                      style={{
                        width: isPhone ? 28 : 32,
                        height: isPhone ? 28 : 32,
                        minWidth: isPhone ? 28 : 32,
                        display: "grid",
                        placeItems: "center",
                        background: "transparent",
                        border: "1px solid #777",
                        borderRadius: 8,
                        color: "#ddd",
                        cursor: "pointer",
                        padding: 0,
                        lineHeight: 0,
                        overflow: "hidden",                // ⬅️ clip any overflow just in case
                      }}
                    >
                      <svg
                        width={isPhone ? 16 : 18}
                        height={isPhone ? 16 : 18}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                        focusable="false"
                        style={{ display: "block" }}
                      >
                        <g>
                          {/* animate the GROUP, not the root <svg> */}
                          {refreshing[t.symbol] ? (
                            <animateTransform
                              attributeName="transform"
                              type="rotate"
                              from="0 12 12"
                              to="360 12 12"
                              dur="0.9s"
                              repeatCount="indefinite"
                            />
                          ) : null}

                          {/* circular arrow */}
                          <path d="M21 12a9 9 0 1 1-3.3-6.9" />
                          <path d="M21 3v6h-6" />
                        </g>
                      </svg>
                    </button>



                  </div>
                )}

                {/* (Optional) stale_24h note unchanged */}
                {d?.stale_24h && (
                  <div style={{ fontSize: 12, color: '#ff9966', marginBottom: 6 }}>
                    52w stats may be outdated
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'center', margin: '4px 0 8px' }}>
                  <VerticalGauge
                    drawdownPct={drawdownPct}
                    width={gaugeSize.width}
                    height={gaugeSize.height}
                    tooltip={tooltip}
                  />
                </div>

                {isPhone && tooltip && (
                  <div style={{ fontSize: 12, color: '#bbb', marginTop: 4 }}>{tooltip}</div>
                )}

                <button
                  style={{
                    marginTop: 10,
                    width: '100%',
                    background: '#2a2a2a',
                    color: '#fff',
                    border: '1px solid #3a3a3a',
                    borderRadius: 10,
                    padding: '10px 12px'
                  }}
                  onClick={() => removeTicker(t.symbol)}
                >
                  Remove
                </button>
              </div>
            );
          })}
        </div>

        <footer style={{ marginTop: 24, textAlign: 'center', color: '#aaa', paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0))' }}>
          Not financial advice. Free API may be delayed/limited.
        </footer>

        {/* Modal: becomes a bottom sheet on phones */}
        <Modal
          isOpen={searchModalOpen}
          onRequestClose={() => setSearchModalOpen(false)}
          style={{
            content: isPhone
              ? {
                  position: 'absolute',
                  inset: 'auto 0 0 0',
                  borderRadius: '16px 16px 0 0',
                  background: '#2a2a2a',
                  color: '#fff',
                  padding: 16,
                  border: 'none',
                  maxHeight: '75dvh',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0))'
                }
              : {
                  background: '#2a2a2a',
                  color: '#fff',
                  inset: '12% 18%',
                  borderRadius: 12,
                  padding: 16,
                  maxHeight: '80vh',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column'
                },
            overlay: {
              backgroundColor: 'rgba(0,0,0,0.6)',
              zIndex: 10000,
              backdropFilter: 'none',
              WebkitBackdropFilter: 'none'
            }
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <h2 style={{ marginTop: 0, fontSize: isPhone ? 18 : 22 }}>Add symbol</h2>
            <button
              onClick={() => setSearchModalOpen(false)}
              style={{
                background: 'transparent',
                color: '#fff',
                border: '1px solid #555',
                borderRadius: 10,
                padding: '8px 10px'
              }}
            >
              Close
            </button>
          </div>

          <input
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: 10,
              marginBottom: 10,
              background: '#1b1b1b',
              color: '#fff',
              border: '1px solid #3a3a3a',
              borderRadius: 10
            }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Try AAPL, SPY, BTCUSD, EURUSD, XAUUSD"
            inputMode="search"
          />

          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <button
              onClick={handleSearch}
              style={{
                background: '#2a2a2a',
                color: '#fff',
                border: '1px solid #3a3a3a',
                borderRadius: 10,
                padding: '10px 12px'
              }}
            >
              Search
            </button>
          </div>

          <div style={{ flex: 1, minHeight: 0 }}>
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                height: '100%',
                overflow: 'auto'
              }}
            >
              {searchResults.map((r, i) => {
                const already = tickers.some(t => t.symbol === r.symbol);
                const disabled = already || tickers.length >= FREE_CAP;
                return (
                  <li
                    key={`${r.type}-${r.symbol}-${i}`}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 0',
                      borderBottom: '1px solid #3a3a3a',
                      gap: 8
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.symbol}</div>
                      <div style={{ fontSize: 12, color: '#bbb' }}>
                        {r.name} {r.region ? `• ${r.region}` : ''} {r.source ? `• ${r.source}` : ''}
                      </div>
                    </div>
                    {r.type === 'crypto' ? (
                      <button
                        disabled={disabled || r._unsupported}
                        onClick={() => addCryptoPair(r.symbol)}
                        title={r._unsupported ? 'Not available on Twelve Data free tier' :
                              tickers.length >= FREE_CAP ? `Free plan allows up to ${FREE_CAP} tickers.` :
                              (already ? 'Added' : 'Add')}
                        style={{
                          background: '#2a2a2a',
                          color: '#fff',
                          border: '1px solid #3a3a3a',
                          borderRadius: 10,
                          padding: '8px 10px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {r._unsupported ? 'Not on free plan' : (already ? 'Added' : 'Add')}
                      </button>
                    ) : r.type === 'fx' ? (
                      <button
                        disabled={disabled || r._unsupported}
                        onClick={() => addFxPair(r.symbol)}
                        title={r._unsupported ? 'Not available on Twelve Data free tier' :
                              tickers.length >= FREE_CAP ? `Free plan allows up to ${FREE_CAP} tickers.` :
                              (already ? 'Added' : 'Add')}
                        style={{
                          background: '#2a2a2a',
                          color: '#fff',
                          border: '1px solid #3a3a3a',
                          borderRadius: 10,
                          padding: '8px 10px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {r._unsupported ? 'Not on free plan' : (already ? 'Added' : 'Add')}
                      </button>
                    ) : (
                      <button
                        disabled={disabled || r._unsupported}
                        onClick={() => addStockTicker(r.symbol)}
                        title={r._unsupported ? 'Not available on Twelve Data free tier' :
                              tickers.length >= FREE_CAP ? `Free plan allows up to ${FREE_CAP} tickers.` :
                              (already ? 'Added' : 'Add')}
                        style={{
                          background: '#2a2a2a',
                          color: '#fff',
                          border: '1px solid #3a3a3a',
                          borderRadius: 10,
                          padding: '8px 10px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {r._unsupported ? 'Not on free plan' : (already ? 'Added' : 'Add')}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </Modal>
      </div>
      <QuickLookModal
        open={!!previewSymbol}
        onClose={() => setPreviewSymbol(null)}
        width={880}
        height={560}
        title={previewSymbol || "Preview"}
      >
        {previewSymbol ? <QuickLookChart symbol={previewSymbol} /> : null}
      </QuickLookModal>
    </div>
  );
};

export default Dashboard;
